import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import CallexLoader from '../../../components/CallexLoader';
import FormSelect from '../../../components/form/FormSelect';
import Pagination from '../../../components/form/Pagination';
import AdminHeader from '../../../components/header/AdminHeader';
import AudioPlayerModal from '../../../components/Modals/AudioPlayerModal';
import { callStatusOptions, checkModulePermission, fileDownload } from '../../../utils/common';

import { useUserRolePermission } from '../../../store/slices/authSlice';
import {
  aiAgentOptionsGet,
  useAiAgentOptions
} from '../../../store/slices/tenantSlices/configurationSlice';
import { callLogsGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import CallDetailsModal from './CallDetailsModal';

const AiCallLogReport = () => {
  const dispatch = useDispatch();
  const aiAgentOptions = useAiAgentOptions();
  const userRolePermission = useUserRolePermission();

  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [audioDetails, setAudioDetails] = useState(null);
  const [callDetails, setCallDetails] = useState(null);
  const [csvReportData, setCsvReportData] = useState([]);
  const [csvHeaderData, setCsvHeaderData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isCsvDownload, setIsCsvDownload] = useState(false);

  const initialValues = {
    page: 1,
    page_size: 10,
    call_status: '',
    agent_id: ''
  };

  const validationSchema = Yup.object({
    agent_id: Yup.string().required('Agent is required')
  });

  const onSubmit = async () => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const payload = {
        ...values
      };

      const response = await dispatch(callLogsGet(payload)).unwrap();
      if (response) {
        setReportData(response);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  useEffect(() => {
    if (values?.agent_id) {
      onSubmit();
    }
  }, [values?.limit, values?.page]);

  const fetchAiAgentOptionsGet = async () => {
    try {
      await dispatch(aiAgentOptionsGet()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    fetchAiAgentOptionsGet();
  }, []);

  function formatTime(value) {
    if (typeof value !== 'string') return '';

    const match = value.match(/^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/);
    if (!match) return '';

    const hours = Math.floor(parseFloat(match[1]));
    const minutes = Math.floor(parseFloat(match[2]));

    if (isNaN(hours) || isNaN(minutes)) return '';

    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  }

  const callStatusColors = {
    completed: '#2cd07e33', // Green
    busy: '#ffb22b33', // Yellow
    failed: '#ff456033', // Red
    'no-answer': '#545b6233' // Gray
  };

  const prepareExportProps = () => {
    const data = csvReportData || [];
    return { headers: csvHeaderData, data, filename: 'call_logs_report.csv' };
  };

  const handleExportCSV = async () => {
    setIsExporting(true);

    try {
      const baseHeaders = [
        { label: 'Call Date', key: 'time_of_call' },
        { label: 'Name', key: 'bot_name' },
        { label: 'From Number', key: 'from_number' },
        { label: 'To Number', key: 'to_number' },
        { label: 'Duration', key: 'call_duration' },
        { label: 'Status', key: 'call_status' }
      ];

      const formattedData = (reportData?.data || []).map((item) => {
        const extractedVars = item?.extracted_variables || {};

        return {
          ...item,
          call_duration: formatTime(item?.call_duration),
          sentiment_score: item?.sentiment_score || '',
          sentiment_analysis_details: item?.sentiment_analysis_details || '',
          ...extractedVars
        };
      });

      const variableHeaders =
        Object.keys(reportData?.data?.[0]?.extracted_variables || {}).map((key) => ({
          label: key.replaceAll('_', ' '),
          key
        })) || [];

      const headers = [
        ...baseHeaders,
        ...variableHeaders,
        { label: 'Sentiment Score', key: 'sentiment_score' },
        { label: 'Analysis Details', key: 'sentiment_analysis_details' }
      ];

      setCsvHeaderData(headers);
      setCsvReportData(formattedData);

      setIsExporting(false);
      setIsCsvDownload(true);
    } catch (error) {
      setIsExporting(false);
      console.log('Error exporting CSV:', error);
    }
  };

  return (
    <div>
      <AdminHeader
        title="AI Call Logs Report"
        isSearch
        isExportDisabled={!checkModulePermission('is_export', 'Call Logs', userRolePermission)}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isExport={reportData?.data?.length > 0}
        csvProps={prepareExportProps()}
        onExportClick={() => handleExportCSV()}
        setIsCsvDownload={setIsCsvDownload}
        isCsvDownload={isCsvDownload}
        isExporting={isExporting}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="4" md="4" className="flex-row">
                <FormSelect
                  name="call_status"
                  label="Call Status"
                  placeholder="Select Call Status"
                  value={values.call_status}
                  options={callStatusOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="4" md="4" className="flex-row">
                <FormSelect
                  name="agent_id"
                  label="AI Agent"
                  placeholder="Select AI Agent"
                  value={values.agent_id}
                  options={aiAgentOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : reportData?.data?.length > 0 ? (
        <Card className="p-0 position-relative">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 369px)' : 'calc(100vh - 208px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Call Date</th>
                  <th>Name</th>
                  <th>From Number</th>
                  <th>To Number</th>
                  <th>Duration</th>
                  <th>Disposition</th>
                  <th className="text-center">Status</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Call Logs
                  </th>
                  <th className="text-center" style={{ width: '120px' }}>
                    Recording
                  </th>
                </tr>
              </thead>
              <tbody>
                {reportData?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.time_of_call}</td>
                      <td>{val?.bot_name}</td>
                      <td>{val?.from_number}</td>
                      <td>{val?.to_number}</td>
                      <td>{formatTime(val?.call_duration)}</td>
                      <td>{val?.disposition || '-'}</td>
                      <td
                        style={{
                          backgroundColor: callStatusColors[val?.call_status] || '#e9ecef',
                          textTransform: 'capitalize',
                          textAlign: 'center'
                        }}>
                        {val?.call_status}
                      </td>
                      <td className="text-center">
                        <Icon.FileText
                          size={16}
                          className="cursor-pointer text-blue"
                          onClick={() => setCallDetails(val)}
                        />
                      </td>
                      <td className="text-center cursor-pointer">
                        {val.recording_url ? (
                          <div className="d-flex justify-content-center gap-4">
                            <Icon.PlayCircle
                              size={16}
                              onClick={() =>
                                setAudioDetails({
                                  src: val.recording_url
                                })
                              }
                              className="cursor-pointer text-info"
                            />
                            <Icon.Download
                              size={16}
                              onClick={() => {
                                let requestUrl = val.recording_url;
                                const separator = val.recording_url.includes('?') ? '&' : '?';
                                requestUrl = `${requestUrl}${separator}api_key=${process.env.REACT_APP_AI_API_KEY}`;
                                fileDownload(requestUrl);
                              }}
                            />
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={reportData?.total}
              limit={reportData?.limit}
              page={reportData?.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => onSubmit()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      {audioDetails && (
        <AudioPlayerModal
          modalOpen={!!audioDetails}
          handleClose={() => setAudioDetails(null)}
          details={audioDetails}
          isToken={true}
        />
      )}
      {!!callDetails && (
        <CallDetailsModal
          isOpen={!!callDetails}
          call_log_id={callDetails?.id}
          onClose={() => {
            setCallDetails(null);
          }}
        />
      )}
    </div>
  );
};

export default AiCallLogReport;
